<template>
    <div class="referral-prescription">
        <div class="referral-prescription__header">
            <div class="referral-prescription__logo">
                <img src="/img/icons/pdf-icon.png" alt="MedMatch Logo" />
            </div>
        </div>

        <div class="referral-prescription__title">
            <common-icon name="eprescribe" size="medium"></common-icon>
            <h2>REFERRAL PRESCRIPTION</h2>
        </div>

        <div class="referral-prescription__provider-info">
            <p class="referral-prescription__provider-name">{{ providerFullName }}</p>
            <p>{{ practiceName }}</p>
            <p>{{ practiceAddress }}</p>
            <p>
                <span v-if="practicePhone">Tel: {{ practicePhone }}</span>
                <span v-if="practiceFax">| Fax: {{ practiceFax }}</span>
            </p>
            <p v-if="providerNpi">NPI: {{ providerNpi }}</p>
        </div>

        <div class="referral-prescription__info-grid">
            <table class="referral-prescription__table">
                <tr>
                    <td class="referral-prescription__label">Date:</td>
                    <td>{{ referralDate }}</td>
                    <td class="referral-prescription__label">Time:</td>
                    <td>{{ referralTime }}</td>
                </tr>
                <tr>
                    <td class="referral-prescription__label">Patient Name:</td>
                    <td colspan="3">{{ patientFullName }}</td>
                </tr>
                <tr>
                    <td class="referral-prescription__label">Phone:</td>
                    <td>{{ patientPhone }}</td>
                    <td class="referral-prescription__label">DOB:</td>
                    <td>{{ patientDOB }}</td>
                </tr>
                <tr v-if="patientAddress">
                    <td class="referral-prescription__label">Address:</td>
                    <td colspan="3">{{ patientAddress }}</td>
                </tr>
                <tr>
                    <td class="referral-prescription__label">Insurance:</td>
                    <td colspan="3">{{ insuranceName }}</td>
                </tr>
                <tr>
                    <td class="referral-prescription__label">Insurance Authorization:</td>
                    <td colspan="3">{{ insuranceAuthStatus }}</td>
                </tr>
            </table>
        </div>

        <div class="referral-prescription__section">
            <p>
                <strong>Diagnosis:</strong>
                {{ diagnosis }}
            </p>
        </div>

        <div v-if="hasFacilityServiceTests" class="referral-prescription__section">
            <h4>Services and Associated Tests</h4>
            <table class="referral-prescription__services-table">
                <thead>
                    <tr>
                        <th>Occupational Therapy Service</th>
                        <th>Tests</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{{ serviceType }}</td>
                        <td>{{ testsDisplay }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="referral-prescription__section">
            <p>
                <strong>Referral To:</strong>
                {{ referralTo }}
            </p>
        </div>

        <div class="referral-prescription__status">
            <p>
                <strong>Status:</strong>
                <span class="referral-prescription__status-badge">{{ referralStatus }}</span>
            </p>
        </div>

        <div class="referral-prescription__signature">
            <p class="referral-prescription__signature-text">{{ providerSignature }}</p>
            <p class="referral-prescription__signature-note">
                <common-icon name="check-circle" size="small"></common-icon>
                {{ providerFullName }} - Digital Signature on File
            </p>
        </div>

        <div class="referral-prescription__print-button">
            <common-button label="Print Prescription" type="primary" @click="printPrescription"></common-button>
        </div>
    </div>
</template>

<script>
    import dayjs from 'dayjs'

    export default {
        props: {
            referral: { type: Object, required: true },
        },

        computed: {
            providerFullName() {
                const user = this.referral.fromUser || {}
                return `${user.firstName || ''} ${user.lastName || ''} ${user.designation}, `.trim()
            },

            hasFacilityServiceTests() {
                const tests = this.referral.facilityServiceTests
                return Array.isArray(tests) && tests.length > 0
            },

            practiceName() {
                return this.referral.fromPractice?.name || ''
            },

            practiceAddress() {
                const location = this.referral.fromPracticeLocation || this.referral.fromPractice?.mainLocation || {}
                const parts = []
                if (location.addressLine1) parts.push(location.addressLine1)
                if (location.addressLine2) parts.push(location.addressLine2)
                if (location.city) parts.push(location.city)
                if (location.state) parts.push(location.state)
                if (location.zipCode) parts.push(location.zipCode)
                return parts.join(', ')
            },

            practicePhone() {
                const location = this.referral.fromPracticeLocation || this.referral.fromPractice?.mainLocation || {}
                return location.phone || this.referral.fromPractice?.phone || ''
            },

            practiceFax() {
                const location = this.referral.fromPracticeLocation || this.referral.fromPractice?.mainLocation || {}
                return location.fax || this.referral.fromPractice?.fax || ''
            },

            providerNpi() {
                return this.referral.fromUser?.npiNumber || ''
            },

            referralDate() {
                return dayjs(this.referral.createdDateTime || this.referral.appointmentDateTime).format('MMMM DD, YYYY')
            },

            referralTime() {
                return dayjs(this.referral.appointmentDateTime).format('h:mm A')
            },

            patientFullName() {
                const patient = this.referral.patient || {}
                return `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
            },

            patientPhone() {
                return this.referral.patient?.mobilePhone || this.referral.patient?.phone || 'Not provided'
            },

            patientDOB() {
                return this.referral.patient?.dateOfBirth ? dayjs(this.referral.patient.dateOfBirth).format('MM/DD/YYYY') : ''
            },

            patientAddress() {
                const patient = this.referral.patient
                if (!patient) return ''

                const { address, city, state, zip } = patient

                const fullAddress = [address, city, state, zip].filter((item) => item && item.trim() !== '').join(', ')

                return fullAddress || ''
            },

            insuranceName() {
                return this.referral.insurancePlan?.name || 'Not required'
            },

            insuranceAuthStatus() {
                return this.referral.isInsuranceAuthorizationRequired ? 'Yes required' : 'Not required'
            },

            diagnosis() {
                return this.referral.diagnosis || 'Type 2 diabetes mellitus with foot ulcer'
            },

            serviceType() {
                // For now, do not display any service name; only show Tests
                return '--'
            },

            testsDisplay() {
                const tests = this.referral.facilityServiceTests || this.referral.tests || []
                return tests.length ? tests.map((item) => `${item.name} - ${item.group?.name || ''}`).join(', ') : ''
            },

            referralTo() {
                const toPractice = this.referral.toUser || ''
                const fullname = `${toPractice.firstName || ''} ${toPractice.lastName || ''}`
                return fullname || ' '
            },

            referralStatus() {
                const statusKey = this.referral && (this.referral.status?.name || this.referral.status)
                if (statusKey) {
                    const translated = this.$t(`pages.referrals.tracking.status_title.${statusKey}`)
                    if (translated && typeof translated === 'string' && translated.length) return translated
                }
                return 'Confirmed'
            },

            providerSignature() {
                return this.providerFullName
            },
        },

        methods: {
            printPrescription() {
                // Get the prescription HTML content
                const prescriptionElement = this.$el
                const prescriptionHTML = prescriptionElement.innerHTML

                // Create a new window for printing
                const printWindow = window.open('', '_blank', 'width=800,height=600')

                if (!printWindow) {
                    alert('Please allow popups to print the prescription')
                    return
                }

                // Write the complete HTML document
                printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <title>Referral Prescription - ${this.patientFullName}</title>
                        <style>
                            * {
                                margin: 0;
                                padding: 0;
                                box-sizing: border-box;
                            }

                            body {
                                font-family: Arial, sans-serif;
                                background: white;
                                padding: 20px;
                            }

                            .referral-prescription {
                                background: white;
                                padding: 40px;
                                max-width: 800px;
                                margin: 0 auto;
                                color: #333;
                            }

                            .referral-prescription__header {
                                text-align: center;
                                margin-bottom: 20px;
                            }

                            .referral-prescription__logo {
                                display: flex;
                                justify-content: center;
                                align-items: center;
                            }

                            .referral-prescription__logo svg {
                                fill: #4a90e2;
                                width: 68px;
                                height: 52px;
                            }

                            .referral-prescription__title {
                                text-align: center;
                                margin-bottom: 30px;
                                display: block;
                            }

                            .referral-prescription__title h2 {
                                margin: 0;
                                font-size: 20px;
                                font-weight: bold;
                                color: #333;
                                text-align: center;
                            }

                            /* Hide icons that may not load properly */
                            .referral-prescription__title .icon,
                            .referral-prescription__title [class*="icon"] {
                                display: none;
                            }

                            .referral-prescription__provider-info {
                                text-align: center;
                                margin-bottom: 30px;
                                border-bottom: 2px solid #333;
                                padding-bottom: 20px;
                            }

                            .referral-prescription__provider-info p {
                                margin: 4px 0;
                                font-size: 14px;
                            }

                            .referral-prescription__provider-name {
                                font-weight: bold;
                                font-size: 16px;
                            }

                            .referral-prescription__info-grid {
                                margin-bottom: 20px;
                            }

                            .referral-prescription__table {
                                width: 100%;
                                border-collapse: collapse;
                                margin-bottom: 20px;
                            }

                            .referral-prescription__table td {
                                padding: 8px;
                                border: 1px solid #ddd;
                                font-size: 14px;
                            }

                            .referral-prescription__label {
                                font-weight: bold;
                                background-color: #f5f5f5;
                                width: 25%;
                            }

                            .referral-prescription__section {
                                margin-bottom: 20px;
                            }

                            .referral-prescription__section h4 {
                                margin: 10px 0;
                                font-size: 15px;
                                font-weight: bold;
                            }

                            .referral-prescription__section p {
                                margin: 8px 0;
                                font-size: 14px;
                                line-height: 1.5;
                            }

                            .referral-prescription__services-table {
                                width: 100%;
                                border-collapse: collapse;
                                margin-top: 10px;
                            }

                            .referral-prescription__services-table th,
                            .referral-prescription__services-table td {
                                padding: 10px;
                                border: 1px solid #ddd;
                                text-align: left;
                                font-size: 14px;
                            }

                            .referral-prescription__services-table th {
                                background-color: #f5f5f5;
                                font-weight: bold;
                            }

                            .referral-prescription__status {
                                margin: 20px 0;
                            }

                            .referral-prescription__status-badge {
                                display: inline-flex;
                                align-items: center;
                                padding: 4px 12px;
                                background-color: #4caf50;
                                color: white;
                                border-radius: 4px;
                                font-size: 13px;
                                margin-left: 10px;
                            }

                            .referral-prescription__status-badge::before {
                                content: '✓';
                                margin-right: 5px;
                            }

                            .referral-prescription__signature {
                                margin-top: 30px;
                                padding: 20px;
                                background-color: #f9f9f9;
                                border: 1px solid #ddd;
                                border-radius: 4px;
                            }

                            .referral-prescription__signature p {
                                margin: 8px 0;
                                font-size: 14px;
                            }

                            .referral-prescription__signature-text {
                                font-style: italic;
                                color: #555;
                                margin: 15px 0;
                            }

                            .referral-prescription__signature-note {
                                display: flex;
                                align-items: center;
                                gap: 8px;
                                color: #4caf50;
                                font-size: 13px;
                            }

                            /* Hide icons that don't render properly in print window */
                            .referral-prescription__signature-note .icon,
                            .referral-prescription__signature-note [class*="icon"] {
                                display: none;
                            }

                            /* Add checkmark before signature note using text */
                            .referral-prescription__signature-note::before {
                                content: '✓';
                                color: #4caf50;
                                font-weight: bold;
                                font-size: 16px;
                            }

                            .referral-prescription__print-button {
                                display: none;
                            }

                        
                            @media print {
                                body {
                                    padding: 0;
                                }

                                .referral-prescription {
                                    padding: 20px;
                                }

                                @page {
                                    size: A4;
                                    margin: 1cm;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        ${prescriptionHTML}
                    </body>
                    </html>
                `)

                printWindow.document.close()

                // Wait for content to load, then print
                printWindow.onload = function () {
                    setTimeout(() => {
                        printWindow.focus()
                        printWindow.print()
                        // Optionally close the window after printing
                        // printWindow.close()
                    }, 250)
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    .referral-prescription {
        background: white;
        padding: 40px;
        max-width: 800px;
        margin: 0 auto;
        font-family: 'Arial', sans-serif;
        color: #333;

        &__header {
            text-align: center;
            margin-bottom: 20px;
        }

        &__logo {
            svg {
                fill: #4a90e2;
            }
        }

        &__title {
            text-align: center;
            margin-bottom: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;

            h2 {
                margin: 0;
                font-size: 20px;
                font-weight: bold;
                color: #333;
            }
        }

        &__provider-info {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;

            p {
                margin: 4px 0;
                font-size: 14px;
            }

            &-name {
                font-weight: bold;
                font-size: 16px;
            }
        }

        &__info-grid {
            margin-bottom: 20px;
        }

        &__table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;

            td {
                padding: 8px;
                border: 1px solid #ddd;
                font-size: 14px;
            }

            &-label {
                font-weight: bold;
                background-color: #f5f5f5;
                width: 25%;
            }
        }

        &__section {
            margin-bottom: 20px;

            h4 {
                margin: 10px 0;
                font-size: 15px;
                font-weight: bold;
            }

            p {
                margin: 8px 0;
                font-size: 14px;
                line-height: 1.5;
            }
        }

        &__services-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;

            th,
            td {
                padding: 10px;
                border: 1px solid #ddd;
                text-align: left;
                font-size: 14px;
            }

            th {
                background-color: #f5f5f5;
                font-weight: bold;
            }
        }

        &__status {
            margin: 20px 0;

            &-badge {
                display: inline-flex;
                align-items: center;
                padding: 4px 12px;
                background-color: #4caf50;
                color: white;
                border-radius: 4px;
                font-size: 13px;
                margin-left: 10px;

                &::before {
                    content: '✓';
                    margin-right: 5px;
                }
            }
        }

        &__signature {
            margin-top: 30px;
            padding: 20px;
            background-color: #f9f9f9;
            border: 1px solid #ddd;
            border-radius: 4px;

            p {
                margin: 8px 0;
                font-size: 14px;
            }

            &-text {
                font-style: italic;
                color: #555;
                margin: 15px 0;
            }

            &-note {
                display: flex;
                align-items: center;
                gap: 8px;
                color: #4caf50;
                font-size: 13px;
            }
        }

        &__print-button {
            margin-top: 30px;
            text-align: center;
        }
    }

    @media print {
        /* Hide everything on the page first */
        body * {
            visibility: hidden !important;
            overflow: visible !important;
        }

        /* Show only the referral prescription and all its children */
        .referral-prescription,
        .referral-prescription *,
        .referral-prescription svg,
        .referral-prescription svg * {
            visibility: visible !important;
        }

        /* Position the prescription at the top of the page for printing */
        .referral-prescription {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 20px !important;
            margin: 0 !important;
            background: white !important;
            box-shadow: none !important;
        }

        /* Hide the print button when printing */
        .referral-prescription__print-button {
            display: none !important;
            visibility: hidden !important;
        }

        /* Ensure tables print properly */
        .referral-prescription__table,
        .referral-prescription__services-table {
            page-break-inside: avoid;
        }

        /* Ensure icons and svgs display correctly */
        .referral-prescription__logo svg,
        .referral-prescription svg {
            display: block !important;
            visibility: visible !important;
        }

        /* Page settings */
        @page {
            size: A4 portrait;
            margin: 1cm;
        }
    }
</style>
